import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormField, Input, Select } from '@vwfs-bronson/bronson-react';
import { useNavigate } from 'react-router-dom';
import { AuthTokens } from '../../auth';
import { Agent, FesConfig, Job, JobFilters, Pagination, Team, getAgents, getAllTeams } from '../../fes';
import { JobDetailPanel } from './JobDetailPanel';
import { JobsTable } from './JobsTable';

const JOB_STATUS_OPTIONS = ['QUEUED', 'STARTING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED', 'PURGED'];
const JOB_SOURCE_OPTIONS = ['api', 'azure-devops', 'chat', 'cli', 'github-pr', 'jira', 'pipeline', 'schedule'];

export function JobsView({
  fesConfig,
  tokens,
  jobs,
  pagination,
  onPageChange,
}: {
  fesConfig: FesConfig;
  tokens: AuthTokens;
  jobs: Job[];
  pagination?: Pagination;
  onPageChange: (offset: number, filters?: JobFilters) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [agentsById, setAgentsById] = useState<Map<string, Agent>>(new Map());
  const [teamsById, setTeamsById] = useState<Map<string, Team>>(new Map());
  const [areLookupsLoading, setAreLookupsLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const onPageChangeRef = useRef(onPageChange);
  const hasMountedFilters = useRef(false);
  const filterRequestId = useRef(0);

  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  useEffect(() => {
    let cancelled = false;

    setAreLookupsLoading(true);
    setLookupError(undefined);

    const loadLookups = async () => {
      try {
        const [agentsResponse, teams] = await Promise.all([
          getAgents(fesConfig, tokens),
          getAllTeams(fesConfig, tokens),
        ]);

        if (cancelled) {
          return;
        }

        setAgentsById(new Map((agentsResponse.data || []).map(agent => [agent.agentId, agent])));
        setTeamsById(new Map(teams.map(team => [team.teamId, team])));
      } catch (error) {
        if (!cancelled) {
          setLookupError(error instanceof Error ? error.message : 'Unable to load agent and team names.');
        }
      } finally {
        if (!cancelled) {
          setAreLookupsLoading(false);
        }
      }
    };

    void loadLookups();

    return () => {
      cancelled = true;
    };
  }, [fesConfig, tokens]);

  const selectedAgentName = selectedJob?.agentId ? agentsById.get(selectedJob.agentId)?.name : undefined;
  const selectedTeamName = selectedJob?.teamId ? teamsById.get(selectedJob.teamId)?.name : undefined;

  const filters = useMemo<JobFilters>(() => ({
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(teamFilter ? { teamId: teamFilter } : {}),
    ...(agentFilter ? { agentId: agentFilter } : {}),
    ...(sourceFilter ? { channel: sourceFilter } : {}),
    ...(fromDate ? { from: fromDate } : {}),
  }), [agentFilter, fromDate, search, sourceFilter, statusFilter, teamFilter]);
  const areFiltersActive = Object.keys(filters).length > 0;

  useEffect(() => {
    if (!hasMountedFilters.current) {
      hasMountedFilters.current = true;
      if (!areFiltersActive) {
        return;
      }
    }

    const requestId = ++filterRequestId.current;
    setIsFiltering(true);
    void onPageChangeRef.current(0, filters).finally(() => {
      if (requestId === filterRequestId.current) {
        setIsFiltering(false);
      }
    });
  }, [areFiltersActive, filters]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTeamFilter('');
    setAgentFilter('');
    setSourceFilter('');
    setFromDate('');
  };

  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      await onPageChange(pagination?.offset ?? 0, filters);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <div className="app-job-filters" aria-label="Job filters">
        <FormField labelForId="job-search" labelText="Search" type="input">
          <Input id="job-search" value={search} onChange={event => setSearch(event.target.value)} />
        </FormField>
        <FormField labelForId="job-status-filter" labelText="Status" type="select">
          <Select id="job-status-filter" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
            <Select.Item value="">All statuses</Select.Item>
            {JOB_STATUS_OPTIONS.map(status => <Select.Item key={status} value={status}>{status}</Select.Item>)}
          </Select>
        </FormField>
        <FormField labelForId="job-team-filter" labelText="Team" type="select">
          <Select id="job-team-filter" value={teamFilter} onChange={event => setTeamFilter(event.target.value)}>
            <Select.Item value="">All teams</Select.Item>
            {Array.from(teamsById.values()).map(team => <Select.Item key={team.teamId} value={team.teamId}>{team.name || team.teamId}</Select.Item>)}
          </Select>
        </FormField>
        <FormField labelForId="job-agent-filter" labelText="Agent" type="select">
          <Select id="job-agent-filter" value={agentFilter} onChange={event => setAgentFilter(event.target.value)}>
            <Select.Item value="">All agents</Select.Item>
            {Array.from(agentsById.values()).map(agent => <Select.Item key={agent.agentId} value={agent.agentId}>{agent.name || agent.agentId}</Select.Item>)}
          </Select>
        </FormField>
        <FormField labelForId="job-source-filter" labelText="Source" type="select">
          <Select id="job-source-filter" value={sourceFilter} onChange={event => setSourceFilter(event.target.value)}>
            <Select.Item value="">All sources</Select.Item>
            {JOB_SOURCE_OPTIONS.map(source => <Select.Item key={source} value={source}>{source}</Select.Item>)}
          </Select>
        </FormField>
        <FormField labelForId="job-from-date" labelText="From" type="input">
          <Input id="job-from-date" type="date" value={fromDate} onChange={event => setFromDate(event.target.value)} />
        </FormField>
      </div>
      <div className="app-table-toolbar">
        <span className="app-filter-summary">
          {isFiltering ? 'Updating jobs...' : `Showing ${jobs.length} of ${pagination?.total ?? jobs.length} matching jobs`}
        </span>
        <Button secondary disabled={!areFiltersActive || isFiltering} onClick={clearFilters}>
          Clear filters
        </Button>
        <Button secondary icon="semantic-reload" disabled={isRefreshing} onClick={handleRefresh}>
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
      <JobsTable
        jobs={jobs}
        agentsById={agentsById}
        teamsById={teamsById}
        pagination={pagination}
        selectedJobId={selectedJob?.requestId}
        onSelectJob={setSelectedJob}
        onPageChange={offset => onPageChange(offset, filters)}
      />
      <JobDetailPanel
        job={selectedJob}
        agentName={selectedAgentName}
        teamName={selectedTeamName}
        lookupsLoading={areLookupsLoading}
        lookupError={lookupError}
        fesConfig={fesConfig}
        tokens={tokens}
        onOpenConversation={selectedJob?.origin?.channel === 'chat' ? () => navigate(`/chat/${encodeURIComponent(selectedJob.requestId)}`) : undefined}
        onClose={() => setSelectedJob(null)}
      />
    </>
  );
}
